import { motion } from "framer-motion";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useRef } from "react";
import { useInView } from "framer-motion";

// Custom hook for scroll-triggered animations
function useScrollAnimation(options = { once: false, amount: 0.15 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, options);
  return { ref, isInView };
}

export default function WebDesign() {
  const heroRef = useScrollAnimation({ once: false, amount: 0.01 });
  const featuresRef = useScrollAnimation();
  const processRef = useScrollAnimation();
  const portfolioRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();

  const features = [
    {
      title: "Responsive Design",
      description:
        "Mobile-first approach ensuring your website looks perfect on all devices",
      icon: "fas fa-mobile-alt",
      gradient: "from-blue-500 to-cyan-400",
    },
    {
      title: "Modern UI/UX",
      description:
        "Contemporary design principles focused on user experience and engagement",
      icon: "fas fa-paint-brush",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      title: "Cross-browser Compatibility",
      description:
        "Consistent performance across all major browsers and platforms",
      icon: "fas fa-globe",
      gradient: "from-green-500 to-emerald-400",
    },
    {
      title: "SEO Optimized",
      description:
        "Built-in SEO best practices to improve your search engine rankings",
      icon: "fas fa-search",
      gradient: "from-orange-500 to-yellow-400",
    },
    {
      title: "Fast Loading",
      description:
        "Optimized for speed with clean code and efficient resource management",
      icon: "fas fa-bolt",
      gradient: "from-red-500 to-orange-400",
    },
    {
      title: "Custom Graphics",
      description:
        "Unique visual elements and graphics tailored to your brand identity",
      icon: "fas fa-image",
      gradient: "from-indigo-500 to-purple-400",
    },
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Research",
      description:
        "We understand your business goals, target audience, and design preferences to create a comprehensive design strategy.",
      color: "bg-gradient-to-br from-blue-600 to-cyan-500",
    },
    {
      step: "02",
      title: "Wireframing & Prototyping",
      description:
        "Create detailed wireframes and interactive prototypes to visualize the user journey and site structure.",
      color: "bg-gradient-to-br from-purple-600 to-pink-500",
    },
    {
      step: "03",
      title: "Visual Design",
      description:
        "Develop the visual identity with custom graphics, color schemes, typography, and brand-consistent elements.",
      color: "bg-gradient-to-br from-green-600 to-emerald-500",
    },
    {
      step: "04",
      title: "Development & Testing",
      description:
        "Convert designs into responsive, functional websites with thorough testing across devices and browsers.",
      color: "bg-gradient-to-br from-orange-600 to-yellow-500",
    },
    {
      step: "05",
      title: "Launch & Support",
      description:
        "Deploy your website and provide ongoing support, maintenance, and optimization services.",
      color: "bg-gradient-to-br from-red-600 to-orange-500",
    },
  ];

  return (
    <div
      className="min-h-screen bg-white overflow-x-hidden"
      style={{ fontFamily: "'Roboto', sans-serif" }}
    >
      <Navigation />

      {/* Hero Section */}
      <section
        ref={heroRef.ref}
        className="relative flex items-center overflow-hidden bg-[#040814] text-white pt-28 pb-5"
      >
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={
              heroRef.isInView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 50 }
            }
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={
                heroRef.isInView ? { opacity: 1 } : { opacity: 0 }
              }
              transition={{ delay: 0.2 }}
              className="flex items-center justify-center mb-8"
            >
              <Link
                href="/"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                Home
              </Link>
              <i className="fas fa-chevron-right text-gray-500 mx-3 text-sm"></i>
              <Link
                href="/service"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                Services
              </Link>
              <i className="fas fa-chevron-right text-gray-500 mx-3 text-sm"></i>
              <span className="text-cyan-400">Web Design</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={
                heroRef.isInView
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 30 }
              }
              transition={{ delay: 0.4 }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full text-sm font-semibold mb-6">
                PREMIUM WEB DESIGN SERVICES
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold mb-6 leading-tight">
                Create Innovative
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  {" "}
                  Web Designs
                </span>
              </h1>
              <p className="text-xl md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
                Our Company Variable Soft Is One of the Leading web design
                company in India. We Create Innovative Web Designs that
                capture your brand essence and engage your audience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={
                heroRef.isInView
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 20 }
              }
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12"
            >
              <Link href="/getintouch">
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 10px 30px rgba(6, 182, 212, 0.4)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="px-7 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold text-lg shadow-2xl shadow-cyan-500/30 transition-all duration-300 flex items-center gap-3"
                >
                  <i className="fas fa-rocket"></i>
                  Start Your Project
                </motion.button>
              </Link>

              <Link href="/portfolio">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-7 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 flex items-center gap-3"
                >
                  <i className="fas fa-eye"></i>
                  View Our Work
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={featuresRef.ref}
        className="py-20 bg-gradient-to-b from-white to-gray-50"
      >
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              featuresRef.isInView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600 rounded-full text-sm font-semibold mb-4">
              OUR FEATURES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Why We're <span className="text-blue-900">Different</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We combine cutting-edge technology with creative design to
              deliver exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const featureRef = useScrollAnimation();

              return (
                <motion.div
                  key={index}
                  ref={featureRef.ref}
                  initial={{ opacity: 0, y: 30 }}
                  animate={
                    featureRef.isInView
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 30 }
                  }
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300"></div>

                  <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-100 group-hover:border-transparent transition-all duration-300">
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300`}
                    >
                      <i className={`${feature.icon} text-white text-2xl`}></i>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {feature.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        ref={processRef.ref}
        className="py-20 bg-gradient-to-b from-gray-900 to-[#040814]"
      >
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              processRef.isInView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 text-cyan-400 rounded-full text-sm font-semibold mb-4">
              OUR PROCESS
            </span>

            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              How We{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Work
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
              A systematic approach to ensure every project exceeds
              expectations.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 hidden lg:block"></div>

            {process.map((item, index) => {
              const stepRef = useScrollAnimation();

              return (
                <motion.div
                  key={index}
                  ref={stepRef.ref}
                  initial={{
                    opacity: 0,
                    x: index % 2 === 0 ? -50 : 50,
                  }}
                  animate={
                    stepRef.isInView
                      ? { opacity: 1, x: 0 }
                      : {
                          opacity: 0,
                          x: index % 2 === 0 ? -50 : 50,
                        }
                  }
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`flex items-center mb-12 ${
                    index % 2 === 0
                      ? "lg:flex-row"
                      : "lg:flex-row-reverse"
                  }`}
                >
                  <div
                    className={`lg:w-1/2 ${
                      index % 2 === 0 ? "lg:pr-12" : "lg:pl-12"
                    }`}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`${
                        index === 4
                          ? "bg-gradient-to-r from-[#1c1c4a] to-[#6b00b6]"
                          : item.color
                      } rounded-2xl p-8 shadow-2xl`}
                    >
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
                          <span className="text-white font-bold text-xl">
                            {item.step}
                          </span>
                        </div>

                        <h3 className="text-2xl font-bold text-white">
                          {item.title}
                        </h3>
                      </div>

                      <p className="text-white/90 leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>
                  </div>

                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2">
                    <div className="w-6 h-6 rounded-full bg-white border-4 border-blue-500 shadow-lg shadow-blue-500/50"></div>
                  </div>

                  <div className="lg:w-1/2 hidden lg:block"></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section ref={portfolioRef.ref} className="py-20 bg-white text-gray-900">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              portfolioRef.isInView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#1c1c4a]/10 to-[#6b00b6]/10 text-[#6b00b6] rounded-full text-sm font-semibold mb-4">
              OUR WORK
            </span>

            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Featured{" "}
              <span className="bg-gradient-to-r from-[#1c1c4a] to-[#6b00b6] bg-clip-text text-transparent">
                Projects
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Discover how we've helped businesses transform their digital
              presence.
            </p>
          </motion.div>

          <div className="text-center mb-12">
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Want to explore more of our successful projects? We've
              delivered powerful solutions for businesses just like yours.
            </p>

            <Link href="/portfolio">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 15px 35px rgba(107, 0, 182, 0.35)",
                }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-gradient-to-r from-[#1c1c4a] to-[#6b00b6] text-white rounded-xl font-semibold text-lg shadow-xl shadow-purple-500/30 hover:shadow-purple-600/50 transition-all duration-300 flex items-center gap-3 mx-auto"
              >
                Explore Full Portfolio
                <i className="fas fa-arrow-right transition-transform duration-300"></i>
              </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        ref={ctaRef.ref}
        className="relative py-24 overflow-hidden bg-[#050B1D] text-white"
      >
        <div className="absolute inset-0">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 -right-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="absolute inset-0 opacity-[0.03] bg-[url('/noise.png')]"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              ctaRef.isInView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight">
              Ready to Elevate Your{" "}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Digital Presence
              </span>
              ?
            </h2>

            <p className="text-lg md:text-xl text-white/70 mb-12 max-w-2xl mx-auto">
              Let's build something impactful together. Book a free
              consultation and discover how we can grow your business.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
              <Link href="/getintouch">
                <motion.button
                  whileHover={{
                    scale: 1.06,
                    boxShadow: "0 20px 40px rgba(139, 92, 246, 0.35)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-semibold text-lg shadow-xl shadow-purple-500/30 transition-all duration-300 flex items-center gap-3"
                >
                  <i className="fas fa-calendar-check"></i>
                  Book Free Consultation
                </motion.button>
              </Link>

              <Link href="/getintouch">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 bg-transparent border border-white/20 text-white rounded-xl font-semibold text-lg hover:bg-white/5 transition-all duration-300 flex items-center gap-3"
                >
                  <i className="fas fa-phone-alt rotate-90"></i>
                  Contact Us Now
                </motion.button>
              </Link>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={ctaRef.isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.3 }}
              className="text-white/50 mt-10 text-sm flex items-center justify-center gap-2"
            >
              <i className="fas fa-clock"></i>
              Typically replies within 1 hour
            </motion.p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
